import { useState, useCallback, useMemo } from 'react';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';

const DEFAULT_MAILBOX = 'default';

// Virtual pitch rows share the selection id space as "pitch-<id>" (their
// data-conversation-id). Bulk actions split on this: mark-read handles both kinds;
// archive is conversations-only (the bar disables Delete while pitches are selected).
const isPitchSelectionId = (id) => id.startsWith('pitch-');
const pitchIdFrom = (id) => Number(id.slice('pitch-'.length));

/**
 * Hook for managing conversation selection mode and bulk actions
 * Remembers selections per mailbox so switching tabs preserves selections
 *
 * @param {string[]} allConversationIds - All selectable row ids (conversation UUIDs and
 *   "pitch-<id>" virtual-row ids) for Select All
 * @param {function} onRefreshNeeded - Callback to trigger conversation refresh
 * @param {string|null} mailbox - Current mailbox (e.g., "focused", "other", or null for non-media users)
 * @param {function} onPitchesMarkedRead - Callback with the numeric pitch ids just marked read
 * @returns {object} Selection state and handlers
 */
function useSelectionMode(allConversationIds, onRefreshNeeded, mailbox, onPitchesMarkedRead) {
  const [selectionMode, setSelectionMode] = useState(false);

  // Store selections per mailbox: { focused: ['id1', 'id2'], other: ['id3'] }
  const [selectionsByMailbox, setSelectionsByMailbox] = useState({});

  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const selectionKey = mailbox || DEFAULT_MAILBOX;

  // Get current mailbox's selections
  const selectedConversationIds = useMemo(
    () => selectionsByMailbox[selectionKey] || [],
    [selectionsByMailbox, selectionKey],
  );

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => !prev);
    // Clear selections for current mailbox when toggling off
    setSelectionsByMailbox((prev) => ({ ...prev, [selectionKey]: [] }));
  }, [selectionKey]);

  const toggleConversationSelection = useCallback(
    (conversationId) => {
      setSelectionsByMailbox((prev) => {
        const current = prev[selectionKey] || [];
        const updated = current.includes(conversationId)
          ? current.filter((id) => id !== conversationId)
          : [...current, conversationId];
        return { ...prev, [selectionKey]: updated };
      });
    },
    [selectionKey],
  );

  const selectAll = useCallback(() => {
    setSelectionsByMailbox((prev) => ({ ...prev, [selectionKey]: allConversationIds }));
  }, [selectionKey, allConversationIds]);

  const deselectAll = useCallback(() => {
    setSelectionsByMailbox((prev) => ({ ...prev, [selectionKey]: [] }));
  }, [selectionKey]);

  const bulkMarkRead = useCallback(async () => {
    const idsToMark = selectionsByMailbox[selectionKey] || [];
    if (idsToMark.length === 0) return;

    setIsMarkingRead(true);

    try {
      const conversationIds = idsToMark.filter((id) => !isPitchSelectionId(id));
      const pitchIds = idsToMark.filter(isPitchSelectionId).map(pitchIdFrom);

      const [{ errors }, pitchResults] = await Promise.all([
        conversationIds.length > 0 ? qapi.conversations.markReadByIds(conversationIds) : { errors: [] },
        Promise.all(pitchIds.map((id) => qapi.pitches.markReadById(id))),
      ]);

      if (errors.length > 0) {
        qlog.error('Some conversations failed to mark as read:', errors);
      }
      const pitchErrors = pitchResults.filter((r) => r.error);
      if (pitchErrors.length > 0) {
        qlog.error('Some pitches failed to mark as read:', pitchErrors);
      }

      globalThis.Qwoted.showToast({
        toastTitle: 'Success',
        toastBody: `Marked ${idsToMark.length} item(s) as read`,
        variant: 'success',
      });

      setSelectionsByMailbox((prev) => ({ ...prev, [selectionKey]: [] }));
      // Optimistic patch so the unread→read transition shows even for selected convs on
      // pages 2+ (the refetch's page-1 merge wouldn't catch them otherwise).
      const readAt = new Date().toISOString();
      onRefreshNeeded?.({ patches: conversationIds.map((id) => ({ id, fields: { receipt_read_at: readAt } })) });
      if (pitchIds.length > 0) onPitchesMarkedRead?.(pitchIds);
    } catch (error) {
      qlog.error('Failed to mark conversations as read:', error);

      globalThis.Qwoted.showToast({
        toastTitle: 'Error',
        toastBody: 'Failed to mark conversations as read. Please try again.',
        variant: 'danger',
      });
    } finally {
      setIsMarkingRead(false);
    }
  }, [selectionsByMailbox, selectionKey, onRefreshNeeded, onPitchesMarkedRead]);

  const bulkArchive = useCallback(async () => {
    // Defensive: pitches cannot be archived (nothing to trash) — the bar disables Delete
    // while any pitch is selected, but never let a pitch id reach the archive API.
    const idsToArchive = (selectionsByMailbox[selectionKey] || []).filter((id) => !isPitchSelectionId(id));
    if (idsToArchive.length === 0) return;

    setIsArchiving(true);

    try {
      const { errors } = await qapi.conversations.archiveByIds(idsToArchive);

      if (errors.length > 0) {
        qlog.error('Some conversations failed to archive:', errors);
      }

      globalThis.Qwoted.showToast({
        toastTitle: 'Moved to Trash',
        toastBody: `Moved ${idsToArchive.length} conversation(s) to trash`,
        variant: 'success',
      });

      setSelectionMode(false);
      setSelectionsByMailbox((prev) => ({ ...prev, [selectionKey]: [] }));
      onRefreshNeeded?.({ evictedIds: idsToArchive });
    } catch (error) {
      qlog.error('Failed to move conversations to trash:', error);

      globalThis.Qwoted.showToast({
        toastTitle: 'Error',
        toastBody: 'Failed to move conversations to trash. Please try again.',
        variant: 'danger',
      });
    } finally {
      setIsArchiving(false);
    }
  }, [selectionsByMailbox, selectionKey, onRefreshNeeded]);

  return {
    // State
    selectionMode,
    selectedConversationIds,
    selectedCount: selectedConversationIds.length,
    hasPitchesSelected: selectedConversationIds.some(isPitchSelectionId),
    isMarkingRead,
    isArchiving,
    isLoading: isMarkingRead || isArchiving,

    // Actions
    toggleSelectionMode,
    toggleConversationSelection,
    selectAll,
    deselectAll,
    bulkMarkRead,
    bulkArchive,
  };
}

export default useSelectionMode;
